import type { AppQueryBuilderCompileRequest } from "@/features/app-studio/api/appQueryBuilderApi";
import type { AppWidgetInputMode } from "@/features/app-studio/lib/appChartLayout";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type AppKpiFormat = "number" | "percent" | "currency" | "text";

/**
 * Which widget table a KPI's drill-down target lives in. Required alongside the id because
 * charts and tables have independent id sequences, so chart 12 and table 12 both exist.
 */
export type AppKpiDrilldownItemType = "chart" | "table";
/**
 * Same target kinds whichever widget opens the drill-down, so table-column drill-downs reuse
 * this rather than declaring a parallel union.
 */
export type AppDrilldownItemType = AppKpiDrilldownItemType;

/** What happens to the drill-down widget when its KPI card is deleted. */
export type AppKpiDrilldownDeleteAction = "unlink" | "delete";

export type AppKpiSummary = {
  id: number;
  app_id: number;
  name: string;
  /** The KPI's SQL lives on its referenced AppQuery; not returned on the summary. */
  sql?: string | null;
  /** Reference to the reusable AppQuery that supplies this KPI's SQL. */
  query_id?: number | null;
  /** Which column of the single-row result to display; null = the only column. */
  value_column?: string | null;
  format: AppKpiFormat;
  column_span: number;
  sort_order: number;
  tab_id?: number | null;
  input_mode: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** The chart/table on this KPI's tab that opens in a dialog when the card is clicked. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
  created_at: string;
  updated_at: string;
};

export type AppKpiDataItem = {
  id: number;
  name: string;
  format: AppKpiFormat;
  column_span?: number;
  sort_order: number;
  tab_id?: number | null;
  value?: unknown;
  error?: string | null;
  /** Display names of active tab filters that did not apply to this widget (drives the badge). */
  ignored_filters?: string[] | null;
  /**
   * Set by the server only when the pointer resolves to a live widget on this KPI's tab, so
   * "has a drill-down" can be read directly as "this card is clickable".
   */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
  /** Always false for a KPI — only charts and tables can be drilled into. */
  is_drilldown_target?: boolean;
};

export type AppKpisDataResponse = {
  kpis: AppKpiDataItem[];
};

const APP_KPI_FORMATS: readonly AppKpiFormat[] = ["number", "percent", "currency", "text"];

const APP_KPI_DRILLDOWN_ITEM_TYPES: readonly AppKpiDrilldownItemType[] = ["chart", "table"];

function isAppKpiFormat(value: unknown): value is AppKpiFormat {
  return typeof value === "string" && (APP_KPI_FORMATS as readonly string[]).includes(value);
}

/** Each half of the pointer must be absent, null, or the right type. */
function hasValidDrilldownPair(row: Record<string, unknown>): boolean {
  const itemType = row.drilldown_item_type;
  const itemId = row.drilldown_item_id;
  const typeOk =
    itemType === undefined ||
    itemType === null ||
    (typeof itemType === "string" &&
      (APP_KPI_DRILLDOWN_ITEM_TYPES as readonly string[]).includes(itemType));
  const idOk = itemId === undefined || itemId === null || typeof itemId === "number";
  return typeOk && idOk;
}

function isAppKpiSummary(value: unknown): value is AppKpiSummary {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.app_id === "number" &&
    typeof row.name === "string" &&
    (row.sql === null || row.sql === undefined || typeof row.sql === "string") &&
    (row.query_id === undefined || row.query_id === null || typeof row.query_id === "number") &&
    (row.value_column === undefined ||
      row.value_column === null ||
      typeof row.value_column === "string") &&
    isAppKpiFormat(row.format) &&
    typeof row.column_span === "number" &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    (row.input_mode === undefined || typeof row.input_mode === "string") &&
    (row.input_config === undefined ||
      row.input_config === null ||
      typeof row.input_config === "object") &&
    hasValidDrilldownPair(row) &&
    typeof row.created_at === "string" &&
    typeof row.updated_at === "string"
  );
}

export function isAppKpiDataItem(value: unknown): value is AppKpiDataItem {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "number" &&
    typeof row.name === "string" &&
    isAppKpiFormat(row.format) &&
    (row.column_span === undefined || typeof row.column_span === "number") &&
    typeof row.sort_order === "number" &&
    (row.tab_id === undefined || row.tab_id === null || typeof row.tab_id === "number") &&
    hasValidDrilldownPair(row) &&
    (row.error === undefined || row.error === null || typeof row.error === "string")
  );
}

export async function fetchAppKpis(appId: number): Promise<AppKpiSummary[]> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data) || !data.every(isAppKpiSummary)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function fetchAppKpisData(appId: number): Promise<AppKpiDataItem[]> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis/data`, { method: "GET" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (typeof data !== "object" || data === null) {
    throw new Error("Unexpected response shape");
  }
  const kpis = (data as AppKpisDataResponse).kpis;
  if (!Array.isArray(kpis) || !kpis.every(isAppKpiDataItem)) {
    throw new Error("Unexpected response shape");
  }
  return kpis;
}

export type AddKpiToAppPayload = {
  name: string;
  /** Provide exactly one of `sql` (inline) or `query_id` (reuse a saved query). */
  sql?: string;
  query_id?: number | null;
  /** Which column of the single-row result to display; required for multi-column results. */
  value_column?: string | null;
  format: AppKpiFormat;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** Send both or neither; the target must already exist on `tab_id`. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export type UpdateAppKpiPayload = {
  name?: string;
  sql?: string;
  query_id?: number | null;
  value_column?: string | null;
  format?: AppKpiFormat;
  column_span?: number;
  tab_id?: number | null;
  input_mode?: AppWidgetInputMode;
  input_config?: AppQueryBuilderCompileRequest | null;
  /** Send both as null to unlink the drill-down; omit both to leave it unchanged. */
  drilldown_item_type?: AppKpiDrilldownItemType | null;
  drilldown_item_id?: number | null;
};

export async function addKpiToApp(
  appId: number,
  payload: AddKpiToAppPayload,
): Promise<AppKpiSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppKpiSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function updateAppKpi(
  appId: number,
  kpiId: number,
  payload: UpdateAppKpiPayload,
): Promise<AppKpiSummary> {
  const res = await apiFetch(`/v1/apps/${appId}/kpis/${kpiId}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  if (!isAppKpiSummary(data)) {
    throw new Error("Unexpected response shape");
  }
  return data;
}

export async function deleteAppKpi(
  appId: number,
  kpiId: number,
  drilldownAction: AppKpiDrilldownDeleteAction = "unlink",
): Promise<void> {
  const query = `?drilldown_action=${drilldownAction}`;
  const res = await apiFetch(`/v1/apps/${appId}/kpis/${kpiId}${query}`, { method: "DELETE" });
  if (!res.ok) {
    throw new Error(await readApiErrorMessage(res));
  }
}
